/**
 * Migration Service
 *
 * Handles migrating local studio data to cloud when a user upgrades.
 * - Idempotent: safe to re-run
 * - Preserves local backup until explicitly cleared
 * - Only triggers on mode transition (local → cloud)
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { LocalActiveStory } from '@/types/studio'
import type { StepType } from '@/types/database'
import {
  getLocalActiveStory,
  getMigrationState,
  markMigrationStarted,
  markMigrationCompleted,
  markMigrationFailed,
  isMigrationCompleted,
} from './localStudioStore'

export interface MigrationResult {
  success: boolean
  cloudStoryId?: string
  error?: string
  alreadyMigrated?: boolean
}

/**
 * Migrates a child's local studio story to the cloud.
 *
 * Trigger conditions (checked by caller):
 * 1. Previous mode was 'local'
 * 2. New mode is 'cloud'
 * 3. Migration not already completed for this child
 *
 * This function is idempotent - if migration is already completed,
 * it returns the existing cloud story ID.
 */
export async function migrateLocalStudioToCloud(childId: string): Promise<MigrationResult> {
  // Check if already migrated
  if (isMigrationCompleted(childId)) {
    const state = getMigrationState(childId)
    return {
      success: true,
      cloudStoryId: state?.cloudStoryId,
      alreadyMigrated: true,
    }
  }

  // Check if there's anything to migrate
  const localStory = getLocalActiveStory(childId)
  if (!localStory) {
    return { success: true, alreadyMigrated: false }
  }

  // Check Supabase configuration
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' }
  }

  // Mark migration as started
  markMigrationStarted(childId)

  try {
    const cloudStoryId = await migrateStory(childId, localStory)

    // Mark migration as completed
    markMigrationCompleted(childId, cloudStoryId)

    return { success: true, cloudStoryId }
  } catch (err) {
    const error = err instanceof Error ? err.message : 'Unknown migration error'
    markMigrationFailed(childId, error)
    return { success: false, error }
  }
}

/**
 * Internal function to migrate a single story to the cloud.
 */
async function migrateStory(childId: string, localStory: LocalActiveStory): Promise<string> {
  // Create the story in Supabase
  const { data: cloudStory, error: storyError } = await supabase
    .from('stories')
    .insert({
      child_id: childId,
      title: localStory.title,
      cover_emoji: localStory.coverEmoji,
      status: localStory.status,
      word_count: localStory.wordCount,
      // Use local timestamps to preserve history
      created_at: localStory.createdAt,
      updated_at: localStory.updatedAt,
    } as Record<string, unknown>)
    .select('id')
    .single()

  if (storyError) {
    throw new Error(`Failed to create cloud story: ${storyError.message}`)
  }

  const cloudStoryId = cloudStory.id

  // Update the auto-created steps with content from local
  // (story_steps are auto-created by the database trigger)
  const stepTypes: StepType[] = ['beginning', 'problem', 'adventure', 'climax', 'resolution']

  for (const stepType of stepTypes) {
    const stepContent = localStory.steps[stepType]
    if (!stepContent) continue

    const { error: stepError } = await supabase
      .from('story_steps')
      .update({
        content: stepContent.content,
        word_count: stepContent.wordCount,
        updated_at: stepContent.updatedAt || localStory.updatedAt,
        client_rev: Date.now(),
      } as Record<string, unknown>)
      .eq('story_id', cloudStoryId)
      .eq('step_type', stepType)

    if (stepError) {
      console.error(`Failed to migrate step ${stepType}:`, stepError)
      // Continue with other steps rather than failing completely
    }
  }

  return cloudStoryId
}

/**
 * Checks if there's pending local data that would need migration.
 */
export function hasPendingMigration(childId: string): boolean {
  // If already migrated, no pending
  if (isMigrationCompleted(childId)) return false

  // If there's a local story, it's pending
  const localStory = getLocalActiveStory(childId)
  return localStory !== null
}

/**
 * Gets the current migration state for display in UI.
 */
export function getMigrationStatus(childId: string): {
  hasPending: boolean
  isCompleted: boolean
  cloudStoryId?: string
  error?: string
} {
  const state = getMigrationState(childId)
  const localStory = getLocalActiveStory(childId)

  return {
    hasPending: !state?.status || (state.status !== 'completed' && localStory !== null),
    isCompleted: state?.status === 'completed',
    cloudStoryId: state?.cloudStoryId,
    error: state?.status === 'failed' ? state.error : undefined,
  }
}
